export class PollTimeoutError extends Error {
  constructor() {
    super('Polling deadline exceeded')
    this.name = 'PollTimeoutError'
  }
}

export interface PollUntilOptions {
  /** Delay between attempts, in ms. */
  intervalMs: number
  /**
   * Total time budget measured from the first attempt, in ms. The last
   * attempt runs at the deadline itself.
   */
  deadlineMs: number
  /** Injectable for tests; defaults to a real setTimeout sleep. */
  sleep?: (ms: number) => Promise<void>
  /** Injectable for tests; defaults to Date.now. */
  now?: () => number
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Runs `fetchValue` immediately and then every `intervalMs` until `isDone`
 * accepts a result, resolving with that result. Once the next attempt would
 * fall past `deadlineMs`, throws PollTimeoutError instead. Fetch errors
 * propagate unchanged.
 */
export async function pollUntil<T>(
  fetchValue: () => Promise<T>,
  isDone: (value: T) => boolean,
  options: PollUntilOptions,
): Promise<T> {
  const {
    intervalMs,
    deadlineMs,
    sleep = defaultSleep,
    now = Date.now,
  } = options
  const deadline = now() + deadlineMs
  for (;;) {
    const value = await fetchValue()
    if (isDone(value)) return value
    if (now() + intervalMs > deadline) throw new PollTimeoutError()
    await sleep(intervalMs)
  }
}
